import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  History,
  Search,
  Filter,
  RefreshCw,
  Eye,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  ArrowRightLeft,
  Users,
  Percent,
  Copy,
  Building,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Combobox } from '@/components/common/Combobox';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingState } from '@/components/common/LoadingState';
import { formatDate } from '@/lib/utils';
import { ReconciliationIssueRecord, IssueStatus, IssueSeverity } from '@/types/database';
import { reconciliationService, IssueCounts } from '@/services/reconciliationService';
import { GrowerResolutionModal } from '@/components/admin/GrowerResolutionModal';
import { CycleResolutionModal } from '@/components/admin/CycleResolutionModal';
import { RateRuleModal } from '@/components/admin/RateRuleModal';
import { CounterpartyModal } from '@/components/admin/CounterpartyModal';
import { DuplicateModal } from '@/components/admin/DuplicateModal';
import { IssueDetailDrawer } from '@/components/admin/IssueDetailDrawer';

export const ReviewAndResolvePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<IssueStatus>('needs_review');
  const [issues, setIssues] = useState<ReconciliationIssueRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const pageSize = 25;

  const [counts, setCounts] = useState<IssueCounts>({
    needsReview: 0,
    resolved: 0,
    historicalAccepted: 0,
    total: 0,
    critical: 0,
    warning: 0,
    info: 0,
  });

  // Filter States
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Selected issue for modals / drawer
  const [selectedIssue, setSelectedIssue] = useState<ReconciliationIssueRecord | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState<boolean>(false);
  const [growerModalOpen, setGrowerModalOpen] = useState<boolean>(false);
  const [cycleModalOpen, setCycleModalOpen] = useState<boolean>(false);
  const [rateRuleModalOpen, setRateRuleModalOpen] = useState<boolean>(false);
  const [counterpartyModalOpen, setCounterpartyModalOpen] = useState<boolean>(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await reconciliationService.getIssueCounts();
      setCounts(res);
    } catch (err) {
      console.error('Error fetching issue counts:', err);
    }
  }, []);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reconciliationService.getIssues({
        status: activeTab,
        entity_type: entityFilter,
        severity: severityFilter as IssueSeverity,
        search: searchTerm,
        page,
        pageSize,
      });
      setIssues(res.issues);
      setTotalCount(res.totalCount);
    } catch (err) {
      console.error('Error fetching issues:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, entityFilter, severityFilter, searchTerm, page]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, entityFilter, severityFilter, searchTerm]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const handleOpenResolve = (issue: ReconciliationIssueRecord) => {
    setSelectedIssue(issue);
    if (issue.entity_type === 'grower' || issue.entity_type === 'production_unit' || issue.issue_type === 'compound_grower_name') {
      setGrowerModalOpen(true);
    } else if (issue.entity_type === 'production_cycle') {
      setCycleModalOpen(true);
    } else if (issue.entity_type === 'rate_rule' || issue.issue_type === 'unproven_rate_rule_calendar_year') {
      setRateRuleModalOpen(true);
    } else if (issue.entity_type === 'supplier' || issue.entity_type === 'buyer') {
      setCounterpartyModalOpen(true);
    } else if (issue.entity_type === 'transaction_duplicate' || issue.issue_type === 'candidate_duplicate_transaction') {
      setDuplicateModalOpen(true);
    } else {
      // Default to detail drawer if generic
      setDetailDrawerOpen(true);
    }
  };

  const handleOpenDetail = (issue: ReconciliationIssueRecord) => {
    setSelectedIssue(issue);
    setDetailDrawerOpen(true);
  };

  const handleSuccess = () => {
    fetchCounts();
    fetchIssues();
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'grower':
      case 'production_unit':
        return <Users className="w-3.5 h-3.5 text-indigo-600" />;
      case 'production_cycle':
        return <ArrowRightLeft className="w-3.5 h-3.5 text-emerald-600" />;
      case 'rate_rule':
        return <Percent className="w-3.5 h-3.5 text-amber-600" />;
      case 'supplier':
      case 'buyer':
        return <Building className="w-3.5 h-3.5 text-blue-600" />;
      case 'transaction_duplicate':
        return <Copy className="w-3.5 h-3.5 text-purple-600" />;
      default:
        return <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />;
    }
  };

  const entityOptions = [
    { value: 'all', label: 'All Entity Types' },
    { value: 'production_cycle', label: 'Production Cycles' },
    { value: 'grower', label: 'Growers / Pens' },
    { value: 'rate_rule', label: 'Rate Rules' },
    { value: 'supplier', label: 'Suppliers' },
    { value: 'buyer', label: 'Buyers' },
    { value: 'transaction_duplicate', label: 'Duplicates' },
  ];

  const severityOptions = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: 'Critical Only' },
    { value: 'warning', label: 'Warning Only' },
    { value: 'info', label: 'Info Only' },
  ];

  const handleResetFilters = () => {
    setSearchTerm('');
    setEntityFilter('all');
    setSeverityFilter('all');
  };

  const isFiltered = searchTerm !== '' || entityFilter !== 'all' || severityFilter !== 'all';

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* 1. Standard PageHeader */}
      <PageHeader
        title="Review & Resolve Queue"
        subtitle="Native Administration inbox for resolving historical data ambiguities, recent cycles, rate rules, and duplicate candidates."
        icon={ShieldAlert}
        helpScreenKey="review_resolve"
        actions={
          <ActionTooltip content="Reload pending triage queue items and audit counts.">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchCounts();
                fetchIssues();
              }}
              disabled={loading}
              className="text-xs h-9 min-h-[44px] gap-1.5"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
          </ActionTooltip>
        }
      />

      {/* 2. Standard 4-Card KpiCardGroup */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Needs Review"
          value={counts.needsReview.toString()}
          subtitle="Active human triage queue"
          icon={AlertTriangle}
          variant="amber"
        />
        <KpiCard
          title="Critical Severity"
          value={counts.critical.toString()}
          subtitle="Discrepancies & broken refs"
          icon={ShieldAlert}
          variant="rose"
        />
        <KpiCard
          title="Warnings"
          value={counts.warning.toString()}
          subtitle="Ambiguities & rate rules"
          icon={AlertCircle}
          variant="default"
        />
        <KpiCard
          title="Resolved"
          value={counts.resolved.toString()}
          subtitle="Reversible decision logs"
          icon={CheckCircle2}
          variant="emerald"
        />
      </KpiCardGroup>

      {/* 3. 2-Tab Queue Controls & FilterBar */}
      <div className="space-y-3">
        {/* 2-Tab Segmented Controls */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
          <ActionTooltip content="Switch to active queue of records requiring administrative review and confirmation.">
            <button
              onClick={() => {
                setActiveTab('needs_review');
                setPage(1);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors min-h-[44px] whitespace-nowrap ${
                activeTab === 'needs_review'
                  ? 'bg-amber-700 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Needs Review ({counts.needsReview})</span>
            </button>
          </ActionTooltip>

          <ActionTooltip content="Switch to audit log of previously resolved mapping decisions.">
            <button
              onClick={() => {
                setActiveTab('resolved');
                setPage(1);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors min-h-[44px] whitespace-nowrap ${
                activeTab === 'resolved'
                  ? 'bg-emerald-700 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Resolved ({counts.resolved})</span>
            </button>
          </ActionTooltip>
        </div>

        {/* FilterBar with Search and Pickers */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by grower, raw string, cycle code, or description..."
          onReset={handleResetFilters}
          showReset={isFiltered}
        >
          <div className="w-full sm:w-48">
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="w-full text-xs rounded-md border border-slate-300 p-2 bg-white text-slate-800 h-10 min-h-[44px] font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              {entityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full sm:w-44">
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="w-full text-xs rounded-md border border-slate-300 p-2 bg-white text-slate-800 h-10 min-h-[44px] font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              {severityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </FilterBar>
      </div>

      {/* 4. Desktop Table & Mobile Cards */}
      {loading ? (
        <div className="py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
          <LoadingState message="Loading Review & Resolve inbox..." />
        </div>
      ) : issues.length === 0 ? (
        <div className="py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
          <EmptyState
            icon={activeTab === 'resolved' ? CheckCircle2 : activeTab === 'needs_review' ? AlertTriangle : History}
            title={
              activeTab === 'resolved'
                ? 'No Resolved Issues Found'
                : activeTab === 'needs_review'
                ? 'No Active Issues In Queue'
                : 'No Historical Accepted Records'
            }
            description="Try adjusting your entity type, severity, or search filters."
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Desktop Table (md:table) */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/70 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-3.5 w-24">Severity</th>
                    <th className="py-3 px-3.5 w-36">Target Entity</th>
                    <th className="py-3 px-3.5 w-44">Issue Type</th>
                    <th className="py-3 px-3.5">Raw Excel Value / Description</th>
                    <th className="py-3 px-3.5 w-36">Excel Source</th>
                    <th className="py-3 px-3.5 w-28 text-center">Status</th>
                    <th className="py-3 px-3.5 w-32 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {issues.map((issue) => (
                    <tr key={issue.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3.5">
                        <StatusBadge status={issue.severity} category="severity" />
                      </td>
                      <td className="py-3 px-3.5">
                        <div className="flex items-center gap-1.5 font-medium text-slate-800">
                          {getEntityIcon(issue.entity_type)}
                          <span className="capitalize">{issue.entity_type.replace(/_/g, ' ')}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3.5">
                        <span className="font-mono text-[11px] font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                          {issue.issue_type}
                        </span>
                      </td>
                      <td className="py-3 px-3.5 max-w-md">
                        <div className="space-y-0.5">
                          {issue.legacy_value && (
                            <div className="font-mono font-bold text-slate-900 text-xs truncate">
                              {issue.legacy_value}
                            </div>
                          )}
                          <div className="text-slate-600 line-clamp-2 text-[11px]">
                            {issue.description}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3.5">
                        <div className="space-y-0.5">
                          <span className="font-mono text-[10px] font-bold text-slate-700 block">
                            {issue.source_sheet}
                          </span>
                          {issue.source_rows && (
                            <span className="text-[10px] text-slate-500 font-mono">
                              {Array.isArray(issue.source_rows)
                                ? `${issue.source_rows.length} rows`
                                : typeof issue.source_rows === 'string'
                                ? 'Lineage rows'
                                : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3.5 text-center">
                        <StatusBadge status={issue.status} category="issue" />
                      </td>
                      <td className="py-3 px-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {activeTab === 'needs_review' ? (
                            <Button
                              size="sm"
                              onClick={() => handleOpenResolve(issue)}
                              className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-3 shadow-xs"
                            >
                              Resolve
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenDetail(issue)}
                              className="h-8 text-xs gap-1 px-2.5 text-slate-700 border-slate-300"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>View</span>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards (md:hidden) */}
          <div className="md:hidden space-y-3">
            {issues.map((issue) => (
              <div
                key={issue.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={issue.severity} category="severity" />
                    <span className="font-mono text-[11px] font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                      {issue.issue_type}
                    </span>
                  </div>
                  <StatusBadge status={issue.status} category="issue" />
                </div>

                <div className="space-y-1 bg-slate-50 p-3 rounded-lg border border-slate-100 text-xs">
                  <div className="flex items-center gap-1.5 font-semibold text-slate-900">
                    {getEntityIcon(issue.entity_type)}
                    <span className="capitalize">{issue.entity_type.replace(/_/g, ' ')}</span>
                  </div>
                  {issue.legacy_value && (
                    <div className="font-mono font-bold text-slate-900 text-xs break-all">
                      {issue.legacy_value}
                    </div>
                  )}
                  <p className="text-slate-600 text-xs">{issue.description}</p>
                  <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-200">
                    Source: <strong className="text-slate-700 font-mono">{issue.source_sheet}</strong>
                  </div>
                </div>

                <div className="pt-1">
                  {activeTab === 'needs_review' ? (
                    <Button
                      size="sm"
                      onClick={() => handleOpenResolve(issue)}
                      className="w-full h-11 min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold justify-center"
                    >
                      Resolve Ambiguity
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDetail(issue)}
                      className="w-full h-11 min-h-[44px] text-slate-700 border-slate-300 text-xs font-semibold justify-center gap-1.5"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Decision History</span>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Bar */}
          {totalCount > pageSize && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-white rounded-xl border border-slate-200 shadow-sm text-xs">
              <span className="text-slate-500">
                Showing <strong className="text-slate-800">{(page - 1) * pageSize + 1}</strong> to{' '}
                <strong className="text-slate-800">{Math.min(page * pageSize, totalCount)}</strong> of{' '}
                <strong className="text-slate-800">{totalCount}</strong> issues
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-9 min-h-[44px] px-3 gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </Button>
                <span className="px-3 font-mono font-medium text-slate-700">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-9 min-h-[44px] px-3 gap-1"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Decision Modals */}
      <GrowerResolutionModal
        issue={selectedIssue}
        isOpen={growerModalOpen}
        onClose={() => setGrowerModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <CycleResolutionModal
        issue={selectedIssue}
        isOpen={cycleModalOpen}
        onClose={() => setCycleModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <RateRuleModal
        issue={selectedIssue}
        isOpen={rateRuleModalOpen}
        onClose={() => setRateRuleModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <CounterpartyModal
        issue={selectedIssue}
        isOpen={counterpartyModalOpen}
        onClose={() => setCounterpartyModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <DuplicateModal
        issue={selectedIssue}
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <IssueDetailDrawer
        issue={selectedIssue}
        isOpen={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        onOpenResolveModal={handleOpenResolve}
        onReopenSuccess={handleSuccess}
      />
    </div>
  );
};
